import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, Sparkles } from 'lucide-react';
import Section from '../../components/v2/Section';
import V2Hero from '../../components/v2/V2Hero';
import { PrimaryCTA } from '../../components/v2/CTAButton';
import SEO from '../../components/SEO';
import { HERO } from '../../data/heroImages';

// Every module has EITHER a monthly recurring price OR a one-off setup fee.
// Prices are "from" figures used to give the buyer a realistic budget
// range before they book a call. Custom setups confirmed on the fit call.
const MODULES = [
  {
    id: 'podcast-production', category: 'Podcast', to: '/podcast-production',
    name: 'Podcast Production', blurb: 'Editing, mixing, mastering, show notes, publishing. Per episode.',
    priceMonthly: 480, priceLabel: 'from \u00a3480/mo (weekly show)', starter: true,
  },
  {
    id: 'podcast-editing', category: 'Podcast', to: '/podcast-editing',
    name: 'Podcast Editing (only)', blurb: 'Clean audio, per episode. No publishing, no show notes.',
    priceMonthly: 360, priceLabel: 'from \u00a3360/mo (weekly show)',
  },
  {
    id: 'podcast-to-youtube', category: 'Video', to: '/podcast-to-youtube',
    name: 'Podcast to YouTube + 21 Shorts', blurb: '1 YouTube upload + 21 daily Shorts, edited, captioned, uploaded weekly.',
    priceMonthly: 600, priceLabel: 'from \u00a3600/mo',
  },
  {
    id: 'blog-content', category: 'Content', to: '/seo',
    name: 'Weekly SEO Blog Post', blurb: 'One SEO-anchored article per week, from your podcast content.',
    priceMonthly: 240, priceLabel: 'from \u00a3240/mo',
  },
  {
    id: 'seo', category: 'Content', to: '/seo',
    name: 'SEO Service (Ongoing)', blurb: 'Technical + content SEO, title/meta rewrites, IndexNow, monthly reporting.',
    priceMonthly: 480, priceLabel: 'from \u00a3480/mo',
  },
  {
    id: 'community-posts', category: 'Content', to: '/podcast-visibility-package',
    name: '7 Community Posts / Week', blurb: 'LinkedIn, X, Instagram or Facebook. Written and scheduled for you.',
    priceMonthly: 200, priceLabel: 'from \u00a3200/mo',
  },
  {
    id: 'weekly-email', category: 'Content', to: '/podcast-visibility-package',
    name: 'Weekly Email Newsletter', blurb: 'One email per week to your list, written from that week\'s episode.',
    priceMonthly: 120, priceLabel: 'from \u00a3120/mo',
  },
  {
    id: 'website-build', category: 'One-off', to: '/authority-hub',
    name: 'Business Website Build', blurb: 'Podcast, books, YouTube, blog and services \u2014 all on one searchable website. Built to rank.',
    priceOneOff: 495, priceLabel: 'from \u00a3495 one-off',
  },
  {
    id: 'podcast-launch', category: 'One-off', to: '/podcast-launch',
    name: 'Podcast Launch Package', blurb: 'Strategy, artwork, first 5 episodes, distribution setup. Under 30 days.',
    priceOneOff: 2400, priceLabel: 'from \u00a32,400 one-off',
  },
  {
    id: 'audiobook', category: 'Authors', to: '/audiobook-production',
    name: 'Audiobook Production', blurb: 'Editing, mastering, chapter markers, ACX submission. Hourly production work.',
    priceOneOff: 60, priceLabel: 'from \u00a360/hour',
  },
  {
    id: 'self-publishing', category: 'Authors', to: '/self-publishing',
    name: 'Self-Publishing (Amazon)', blurb: 'KDP setup, formatting, cover, ISBN, launch. Scoped per book on the free call.',
    priceOneOff: 195, priceLabel: 'from \u00a3195 (formatting)',
  },
];

// Preset bundles shown as one-click starting points. Every id must exist in MODULES.
const PRESETS = [
  { id: 'just-launching', name: 'Just launching a podcast', ids: ['podcast-launch', 'podcast-production'] },
  { id: 'podcast-only',   name: 'Podcast, done for me',      ids: ['podcast-production'] },
  { id: 'visibility',     name: 'Full weekly visibility',    ids: ['podcast-production', 'podcast-to-youtube', 'blog-content', 'community-posts', 'weekly-email'], recommended: true },
  { id: 'author-mode',    name: 'Author launch',             ids: ['self-publishing', 'audiobook'] },
  { id: 'seo-only',       name: 'SEO only',                  ids: ['seo'] },
];

const currency = (n) => `\u00a3${n.toLocaleString('en-GB')}`;

export default function BuildYourPackage() {
  const [selected, setSelected] = useState(new Set());

  const toggle = (id) => {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const loadPreset = (preset) => {
    setSelected(new Set(preset.ids));
    setTimeout(() => {
      const el = document.querySelector('[data-testid="byp-summary"]');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 60);
  };

  const totals = useMemo(() => {
    let monthly = 0, oneOff = 0;
    for (const m of MODULES) {
      if (!selected.has(m.id)) continue;
      if (m.priceMonthly) monthly += m.priceMonthly;
      if (m.priceOneOff)  oneOff  += m.priceOneOff;
    }
    return { monthly, oneOff, count: selected.size };
  }, [selected]);

  // Group modules by category for cleaner UI
  const grouped = useMemo(() => {
    const g = {};
    for (const m of MODULES) { (g[m.category] ||= []).push(m); }
    return g;
  }, []);

  return (
    <div className="bg-[#F6F8FB] min-h-screen">
      <SEO
        title="Build Your Podcast & Content Package | Audio & Co."
        description="Pick the podcast, YouTube, publishing and website services you need and see a live price. Done for you by Audio & Co., cancel any time."
        keywords={['custom podcast package', 'build your own podcast service', 'a la carte podcast services', 'podcast and seo bundle uk', 'podcast production and youtube', 'audiobook and self publishing package']}
        url="https://audioandco.com/build-your-package"
      />

      <V2Hero image={HERO.cosyDeskNotebook} testId="byp-hero">
        <div className="max-w-3xl">
          <p className="text-[#BC516F] font-semibold text-sm uppercase tracking-widest">Build Your Package</p>
          <h1 className="font-display font-bold text-[#1E2A44] text-4xl sm:text-5xl lg:text-6xl leading-[1.1] tracking-tight mt-3">
            Not everyone needs the full stack on day one.
          </h1>
          <p className="text-lg sm:text-xl text-[#475569] mt-6 leading-relaxed">
            Pick only what you need. Start with podcast editing, then add YouTube next quarter, or launch with the full weekly engine from month one. You&rsquo;ll get a realistic budget in 30 seconds, then we&rsquo;ll confirm the right setup on a quick fit call.
          </p>
        </div>
      </V2Hero>

      {/* PRESET BUNDLES */}
      <Section bg="white" testId="byp-presets">
        <div className="max-w-3xl mb-6">
          <p className="text-[#BC516F] font-semibold text-sm uppercase tracking-widest">Start from a template</p>
          <h2 className="font-display font-bold text-[#1E2A44] text-2xl sm:text-3xl tracking-tight mt-2">Popular starting points</h2>
        </div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          {PRESETS.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => loadPreset(p)}
              className={`text-left rounded-[14px] p-4 border-2 transition ${
                p.recommended ? 'border-[#BC516F] bg-white shadow-[0_10px_30px_rgba(30,42,68,0.06)]' : 'border-[#E2E8F0] bg-white hover:border-[#BC516F]'
              }`}
              data-testid={`byp-preset-${p.id}`}
            >
              <div className="flex items-center gap-2 mb-2">
                {p.recommended && <Sparkles className="w-4 h-4 text-[#BC516F]" />}
                <span className="font-display font-semibold text-[#1E2A44] text-[15px] leading-tight">{p.name}</span>
              </div>
              <span className="text-xs text-[#64748B]">{p.ids.length} module{p.ids.length === 1 ? '' : 's'}</span>
            </button>
          ))}
        </div>
      </Section>

      {/* MODULE PICKER */}
      <Section bg="bg" testId="byp-modules">
        <div className="max-w-3xl mb-6">
          <p className="text-[#BC516F] font-semibold text-sm uppercase tracking-widest">Pick what you need</p>
          <h2 className="font-display font-bold text-[#1E2A44] text-2xl sm:text-3xl tracking-tight mt-2">Every module, priced individually</h2>
        </div>
        <div className="space-y-8">
          {Object.entries(grouped).map(([cat, mods]) => (
            <div key={cat}>
              <h3 className="font-display font-semibold text-[#1E2A44] text-xl mb-3">{cat}</h3>
              <div className="grid gap-3 md:grid-cols-2">
                {mods.map((m) => {
                  const on = selected.has(m.id);
                  return (
                    <label
                      key={m.id}
                      htmlFor={`byp-${m.id}`}
                      className={`cursor-pointer rounded-[14px] border-2 p-4 flex items-start gap-3 transition ${
                        on ? 'border-[#BC516F] bg-white shadow-[0_10px_30px_rgba(30,42,68,0.06)]' : 'border-[#E2E8F0] bg-white hover:border-[#94A3B8]'
                      }`}
                      data-testid={`byp-module-${m.id}`}
                    >
                      <input
                        id={`byp-${m.id}`}
                        type="checkbox"
                        className="mt-1 h-5 w-5 rounded border-[#CBD5E1] text-[#BC516F] focus:ring-[#BC516F]"
                        checked={on}
                        onChange={() => toggle(m.id)}
                        data-testid={`byp-check-${m.id}`}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-baseline justify-between gap-2">
                          <span className="font-display font-semibold text-[#1E2A44]">{m.name}</span>
                          <span className="text-xs font-semibold text-[#1E2A44]">{m.priceLabel}</span>
                        </div>
                        <p className="text-sm text-[#475569] mt-1 leading-snug">{m.blurb}</p>
                        <Link to={m.to} className="text-xs text-[#BC516F] font-semibold hover:underline mt-1 inline-block" onClick={(e) => e.stopPropagation()}>
                          See details <ArrowRight className="w-3 h-3 inline" />
                        </Link>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* STICKY SUMMARY + CTA */}
      <div className="sticky bottom-0 bg-[#1E2A44] text-white shadow-[0_-10px_30px_rgba(30,42,68,0.15)] z-40" data-testid="byp-summary">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-white/60">Your package · {totals.count} module{totals.count === 1 ? '' : 's'}</div>
            <div className="font-display font-bold text-2xl sm:text-3xl mt-1">
              {totals.monthly > 0 && <>{currency(totals.monthly)}<span className="text-base font-normal text-white/70">/mo</span></>}
              {totals.monthly > 0 && totals.oneOff > 0 && <span className="text-white/40"> + </span>}
              {totals.oneOff > 0 && <>{currency(totals.oneOff)}<span className="text-base font-normal text-white/70"> one-off</span></>}
              {totals.count === 0 && <span className="text-white/50 text-base font-normal">Pick a module above</span>}
            </div>
            <div className="text-xs text-white/60 mt-1">Indicative &ldquo;from&rdquo; pricing. Final setup confirmed on a quick fit call.</div>
          </div>
          <PrimaryCTA size="lg" data-testid="byp-cta" disabled={totals.count === 0}>
            Check if we&apos;re a fit <ArrowRight className="w-5 h-5" />
          </PrimaryCTA>
        </div>
      </div>

      {/* WHY */}
      <Section bg="white" testId="byp-why">
        <div className="max-w-3xl">
          <p className="text-[#BC516F] font-semibold text-sm uppercase tracking-widest">Why we build it this way</p>
          <h2 className="font-display font-bold text-[#1E2A44] text-2xl sm:text-3xl tracking-tight mt-2">Grow into the full stack, don&rsquo;t buy it all on day one.</h2>
          <p className="text-[#475569] mt-4 leading-relaxed">
            Most agencies push a one-size package because it&rsquo;s easier to sell. We&rsquo;ve found the opposite works better for small business owners: start with the single service that unblocks you (usually podcast editing or SEO), prove the ROI, then add the next module. Every module here works standalone and every module works together. When you&rsquo;re ready for the full weekly engine, you can move to the <Link to="/podcast-visibility-package" className="text-[#BC516F] underline font-semibold">Podcast Visibility Package</Link> and typically save around 15% versus buying the same modules &agrave; la carte.
          </p>
          <div className="mt-6 flex flex-wrap gap-4">
            <span className="inline-flex items-center gap-1.5 text-[#1E2A44] font-semibold text-sm"><Check className="w-4 h-4 text-[#22C55E]" />No hidden minimums</span>
            <span className="inline-flex items-center gap-1.5 text-[#1E2A44] font-semibold text-sm"><Check className="w-4 h-4 text-[#22C55E]" />Add or drop modules with a month&rsquo;s notice</span>
            <span className="inline-flex items-center gap-1.5 text-[#1E2A44] font-semibold text-sm"><Check className="w-4 h-4 text-[#22C55E]" />One dedicated point of contact</span>
          </div>
        </div>
      </Section>
    </div>
  );
}
